import { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DataTable, type ColumnDef, type PaginationState } from '@/components/data-table';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import {
  useClientLedger,
  useDownloadStatement,
  type LedgerEntry,
  type LedgerEntryType,
  type ClientLedgerParams,
} from './hooks/useClientLedger';
import {
  useAccountClosingHistory,
  useBalanceSummary,
  useCloseAccount,
  useReopenPeriod,
  useDownloadLedgerPdf,
  usePreviewClosingBalance,
  type AccountClosing,
} from './hooks/useAccountClosing';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';

/** Map ledger entry type to badge variant for visual distinction. */
const ENTRY_TYPE_BADGE_MAP: Record<
  LedgerEntryType,
  { variant: 'default' | 'info' | 'warning' | 'success' | 'danger' | 'outline'; label: string }
> = {
  invoice: { variant: 'info', label: 'Invoice' },
  payment: { variant: 'success', label: 'Payment' },
  credit_note: { variant: 'warning', label: 'Credit Note' },
  debit_note: { variant: 'danger', label: 'Debit Note' },
  write_off: { variant: 'outline', label: 'Write-Off' },
  discount: { variant: 'default', label: 'Discount' },
  advance: { variant: 'success', label: 'Advance' },
  adjustment: { variant: 'outline', label: 'Adjustment' },
};

/** Format currency for display. */
function formatCurrency(amount: number): string {
  return amount.toLocaleString('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * ClientLedgerPage — full billing transaction history for a client with
 * date range filter, entry type filter, running balance, PDF ledger/statement download,
 * and account period closing for reconciliation.
 *
 * Requirements: 15.25, Account Closing for reconciliation and reporting
 */
export function ClientLedgerPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();

  // ─── Filter & Pagination State ──────────────────────────────────────────────
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [entryType, setEntryType] = useState<LedgerEntryType | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // ─── Account Closing State ──────────────────────────────────────────────────
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closePeriodStart, setClosePeriodStart] = useState('');
  const [closePeriodEnd, setClosePeriodEnd] = useState('');
  const [closeNotes, setCloseNotes] = useState('');
  const [adjustedBalance, setAdjustedBalance] = useState<string>('');
  const [reopenClosingId, setReopenClosingId] = useState<string | null>(null);
  const [reopenReason, setReopenReason] = useState('');

  const params: ClientLedgerParams = useMemo(
    () => ({
      clientId: clientId ?? '',
      page,
      pageSize,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      entryType: entryType || undefined,
    }),
    [clientId, page, pageSize, dateFrom, dateTo, entryType],
  );

  const { can } = usePermissions();
  const { data, isLoading } = useClientLedger(params);
  const downloadStatement = useDownloadStatement();
  const downloadLedgerPdf = useDownloadLedgerPdf();
  const balanceSummary = useBalanceSummary(clientId ?? '');
  const closingHistory = useAccountClosingHistory({ clientId: clientId ?? '', pageSize: 5 });
  const closeAccount = useCloseAccount();
  const reopenPeriod = useReopenPeriod();
  const closingPreview = usePreviewClosingBalance(clientId ?? '', closePeriodStart, closePeriodEnd);

  // ─── Table Columns ──────────────────────────────────────────────────────────
  const columns: ColumnDef<LedgerEntry>[] = useMemo(
    () => [
      {
        id: 'date',
        header: 'Date',
        sortable: true,
        width: '130px',
        cell: (row) => (
          <span className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
            {new Date(row.date).toLocaleDateString('en-CA')}
          </span>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        width: '110px',
        cell: (row) => {
          const badge = ENTRY_TYPE_BADGE_MAP[row.type] || {
            variant: 'default' as const,
            label: row.type?.replace(/_/g, ' ') || 'Unknown',
          };
          return (
            <Badge variant={badge.variant} size="sm">
              {badge.label}
            </Badge>
          );
        },
      },
      {
        id: 'description',
        header: 'Description',
        width: '50%',
        cell: (row) => (
          <div className="text-sm">
            <span className="text-gray-900 dark:text-gray-100">{row.description}</span>
            {row.referenceNumber && (
              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                #{row.referenceNumber}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'debit',
        header: 'Debit',
        align: 'right',
        cell: (row) =>
          row.debit > 0 ? (
            <span className="font-medium text-red-600 dark:text-red-400">
              {formatCurrency(row.debit)}
            </span>
          ) : (
            <span className="text-gray-400 dark:text-gray-500">—</span>
          ),
      },
      {
        id: 'credit',
        header: 'Credit',
        align: 'right',
        cell: (row) =>
          row.credit > 0 ? (
            <span className="font-medium text-green-600 dark:text-green-400">
              {formatCurrency(row.credit)}
            </span>
          ) : (
            <span className="text-gray-400 dark:text-gray-500">—</span>
          ),
      },
      {
        id: 'balance',
        header: 'Balance',
        align: 'right',
        cell: (row) => (
          <span
            className={
              row.balance > 0
                ? 'font-semibold text-red-600 dark:text-red-400'
                : row.balance < 0
                  ? 'font-semibold text-green-600 dark:text-green-400'
                  : 'font-semibold text-gray-900 dark:text-gray-100'
            }
          >
            {formatCurrency(row.balance)}
          </span>
        ),
      },
    ],
    [],
  );

  // ─── Pagination ─────────────────────────────────────────────────────────────
  const pagination: PaginationState | undefined = data
    ? { page: data.page, pageSize: data.pageSize, total: data.total }
    : undefined;

  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  }, []);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleDownloadStatement = useCallback(() => {
    if (!clientId) return;
    downloadStatement.mutate({
      clientId,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    });
  }, [clientId, dateFrom, dateTo, downloadStatement]);

  const handleDownloadLedgerPdf = useCallback(() => {
    if (!clientId) return;
    downloadLedgerPdf.mutate({
      clientId,
      startDate: dateFrom || undefined,
      endDate: dateTo || undefined,
      entryType: entryType || undefined,
    });
  }, [clientId, dateFrom, dateTo, entryType, downloadLedgerPdf]);

  const handleClearFilters = useCallback(() => {
    setDateFrom('');
    setDateTo('');
    setEntryType('');
    setPage(1);
  }, []);

  const handleCloseAccount = useCallback(() => {
    if (!clientId || !closePeriodStart || !closePeriodEnd) return;
    const adjustedValue = adjustedBalance.trim() !== '' ? parseFloat(adjustedBalance) : null;
    closeAccount.mutate(
      {
        clientId,
        period_start: closePeriodStart,
        period_end: closePeriodEnd,
        notes: closeNotes || undefined,
        adjusted_closing_balance: adjustedValue,
      },
      {
        onSuccess: () => {
          setShowCloseModal(false);
          setClosePeriodStart('');
          setClosePeriodEnd('');
          setCloseNotes('');
          setAdjustedBalance('');
        },
      },
    );
  }, [clientId, closePeriodStart, closePeriodEnd, closeNotes, adjustedBalance, closeAccount]);

  const handleReopenPeriod = useCallback(() => {
    if (!reopenClosingId || !reopenReason) return;
    reopenPeriod.mutate(
      { closingId: reopenClosingId, reason: reopenReason },
      {
        onSuccess: () => {
          setReopenClosingId(null);
          setReopenReason('');
        },
      },
    );
  }, [reopenClosingId, reopenReason, reopenPeriod]);

  const hasActiveFilters = dateFrom || dateTo || entryType;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/client-ledger')}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-surface-dark-secondary dark:hover:text-gray-300"
            aria-label="Back to client list"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
              />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Client Ledger</h1>
            {data?.clientName && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Billing transaction history for {data.clientName}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {can(PERMISSIONS.DOCUMENTS_READ) && (
            <Button
              variant="outline"
              onClick={handleDownloadLedgerPdf}
              loading={downloadLedgerPdf.isPending}
              aria-label="Download ledger as PDF"
            >
              <svg
                className="mr-1.5 h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                />
              </svg>
              Ledger PDF
            </Button>
          )}
          {can(PERMISSIONS.DOCUMENTS_READ) && (
            <Button
              variant="outline"
              onClick={handleDownloadStatement}
              loading={downloadStatement.isPending}
              aria-label="Download statement as PDF"
            >
              <svg
                className="mr-1.5 h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
              Statement
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => setShowCloseModal(true)}
            aria-label="Close account period"
          >
            Close Period
          </Button>
        </div>
      </div>

      {/* Balance Summary */}
      {balanceSummary.data && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <Card padding="sm">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Opening Balance
              </span>
              <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {formatCurrency(balanceSummary.data.opening_balance)}
              </span>
            </div>
          </Card>
          <Card padding="sm">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Closing Balance
              </span>
              <span
                className={`text-lg font-semibold ${
                  balanceSummary.data.closing_balance > 0
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-green-600 dark:text-green-400'
                }`}
              >
                {formatCurrency(balanceSummary.data.closing_balance)}
              </span>
            </div>
          </Card>
          <Card padding="sm">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Last Closed Period
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {balanceSummary.data.last_closed_period
                  ? new Date(balanceSummary.data.last_closed_period).toLocaleDateString('en-CA')
                  : 'None'}
              </span>
            </div>
          </Card>
          <Card padding="sm">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Unclosed Transactions
              </span>
              <Badge
                variant={balanceSummary.data.has_unclosed_transactions ? 'warning' : 'success'}
                size="sm"
              >
                {balanceSummary.data.has_unclosed_transactions ? 'Yes' : 'No'}
              </Badge>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-44">
            <label
              htmlFor="ledger-date-from"
              className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              From
            </label>
            <input
              id="ledger-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
              className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
            />
          </div>
          <div className="w-44">
            <label
              htmlFor="ledger-date-to"
              className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              To
            </label>
            <input
              id="ledger-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
              className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
            />
          </div>
          <div className="w-48">
            <Select
              label="Entry Type"
              value={entryType}
              onChange={(e) => {
                setEntryType(e.target.value as LedgerEntryType | '');
                setPage(1);
              }}
              options={[
                { value: 'invoice', label: 'Invoice' },
                { value: 'payment', label: 'Payment' },
                { value: 'credit_note', label: 'Credit Note' },
                { value: 'debit_note', label: 'Debit Note' },
                { value: 'write_off', label: 'Write-Off' },
                { value: 'discount', label: 'Discount' },
              ]}
              placeholder="All types"
            />
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={handleClearFilters}>
              Clear filters
            </Button>
          )}
        </div>
      </Card>

      {/* Data Table */}
      {/* Opening Balance from Last Closing */}
      {data?.openingBalanceFromClosing != null && (
        <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-800 dark:bg-blue-900/20">
          <div className="flex items-center gap-2">
            <svg
              className="h-4 w-4 text-blue-500"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              Opening Balance (b/f from{' '}
              {data.lastClosedPeriodEnd
                ? new Date(data.lastClosedPeriodEnd).toLocaleDateString('en-CA')
                : 'last closing'}
              )
            </span>
          </div>
          <span className="text-sm font-bold text-blue-800 dark:text-blue-200">
            ${formatCurrency(data.openingBalanceFromClosing)}
          </span>
        </div>
      )}

      <DataTable<LedgerEntry>
        data={data?.entries ?? []}
        columns={columns}
        keyExtractor={(row) => row.id}
        sort={{ column: 'date', direction: 'desc' }}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={isLoading}
        emptyMessage="No billing transactions found for this client."
      />

      {/* Account Closing History */}
      {closingHistory.data && closingHistory.data.length > 0 && (
        <Card padding="md">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
            Period Closing History
          </h2>
          <div className="space-y-3">
            {closingHistory.data.map((closing: AccountClosing) => (
              <div
                key={closing.id}
                className="flex items-center justify-between rounded-lg border border-border p-3 dark:border-border-dark"
              >
                <div className="flex items-center gap-4">
                  <Badge variant={closing.status === 'closed' ? 'success' : 'warning'} size="sm">
                    {closing.status === 'closed' ? 'Closed' : 'Reopened'}
                  </Badge>
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {new Date(closing.period_start).toLocaleDateString('en-CA')} to{' '}
                      {new Date(closing.period_end).toLocaleDateString('en-CA')}
                    </span>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Opening: {formatCurrency(closing.opening_balance)} | Closing:{' '}
                      {formatCurrency(
                        closing.adjusted_closing_balance != null
                          ? closing.adjusted_closing_balance
                          : closing.closing_balance,
                      )}
                      {closing.adjusted_closing_balance != null && (
                        <span className="ml-1 text-amber-600 dark:text-amber-400">
                          (adjusted from {formatCurrency(closing.closing_balance)})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {closing.status === 'closed' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setReopenClosingId(closing.id)}
                    >
                      Reopen
                    </Button>
                  )}
                  {closing.status === 'reopened' && closing.reopen_reason && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Reason: {closing.reopen_reason}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Close Period Modal */}
      {showCloseModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="close-period-title"
        >
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-surface-dark">
            <h3
              id="close-period-title"
              className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              Close Account Period
            </h3>
            <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
              Closing a period captures a snapshot of the opening and closing balance for
              reconciliation. You can adjust the closing balance — the adjusted amount will be
              treated as the opening balance for the next period.
            </p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="close-period-start"
                    className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Period Start
                  </label>
                  <input
                    id="close-period-start"
                    type="date"
                    value={closePeriodStart}
                    onChange={(e) => {
                      setClosePeriodStart(e.target.value);
                      setAdjustedBalance('');
                    }}
                    className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                  />
                </div>
                <div>
                  <label
                    htmlFor="close-period-end"
                    className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Period End
                  </label>
                  <input
                    id="close-period-end"
                    type="date"
                    value={closePeriodEnd}
                    onChange={(e) => {
                      setClosePeriodEnd(e.target.value);
                      setAdjustedBalance('');
                    }}
                    className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                  />
                </div>
              </div>

              {/* Computed Balance Preview */}
              {closePeriodStart && closePeriodEnd && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                  <div className="mb-2 text-xs font-medium uppercase text-blue-600 dark:text-blue-400">
                    System Calculated
                  </div>
                  {closingPreview.isLoading ? (
                    <div className="text-sm text-gray-500">Calculating...</div>
                  ) : closingPreview.data ? (
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Debits: </span>
                        <span className="font-medium text-red-600">
                          {formatCurrency(closingPreview.data.total_debits)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Credits: </span>
                        <span className="font-medium text-green-600">
                          {formatCurrency(closingPreview.data.total_credits)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Balance: </span>
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                          {formatCurrency(closingPreview.data.current_balance)}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Adjustable Closing Balance */}
              <div>
                <label
                  htmlFor="close-adjusted-balance"
                  className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Closing Balance
                  <span className="ml-1 text-xs font-normal text-gray-400">
                    (leave empty to use system-calculated value)
                  </span>
                </label>
                <input
                  id="close-adjusted-balance"
                  type="number"
                  step="0.01"
                  value={adjustedBalance}
                  onChange={(e) => setAdjustedBalance(e.target.value)}
                  placeholder={
                    closingPreview.data ? closingPreview.data.current_balance.toFixed(2) : '0.00'
                  }
                  className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                />
                {adjustedBalance.trim() !== '' && closingPreview.data && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    Adjustment:{' '}
                    {formatCurrency(
                      parseFloat(adjustedBalance) - closingPreview.data.current_balance,
                    )}{' '}
                    from calculated. This adjusted amount will be the opening balance for the next
                    period.
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="close-period-notes"
                  className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Notes (optional)
                </label>
                <textarea
                  id="close-period-notes"
                  value={closeNotes}
                  onChange={(e) => setCloseNotes(e.target.value)}
                  rows={2}
                  className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                  placeholder="e.g., Q2 2026 period closing"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowCloseModal(false);
                  setClosePeriodStart('');
                  setClosePeriodEnd('');
                  setCloseNotes('');
                  setAdjustedBalance('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCloseAccount}
                loading={closeAccount.isPending}
                disabled={!closePeriodStart || !closePeriodEnd}
              >
                Close Period
              </Button>
            </div>
            {closeAccount.isError && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                {(closeAccount.error as Error)?.message || 'Failed to close period'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Reopen Period Modal */}
      {reopenClosingId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reopen-period-title"
        >
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-surface-dark">
            <h3
              id="reopen-period-title"
              className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              Reopen Closed Period
            </h3>
            <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
              Reopening a period allows modifications to transactions within that date range. A
              reason is required for audit purposes.
            </p>
            <div>
              <label
                htmlFor="reopen-reason"
                className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Reason
              </label>
              <textarea
                id="reopen-reason"
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                rows={3}
                className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                placeholder="e.g., Missed payment needs to be recorded"
              />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setReopenClosingId(null);
                  setReopenReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleReopenPeriod}
                loading={reopenPeriod.isPending}
                disabled={!reopenReason.trim()}
              >
                Reopen Period
              </Button>
            </div>
            {reopenPeriod.isError && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                {(reopenPeriod.error as Error)?.message || 'Failed to reopen period'}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
